import React, { useState, useCallback, useMemo } from "react";
import { Layout, Menu, ConfigProvider, Button } from "antd";
import { useLocation, useNavigate, Outlet } from "react-router-dom";
import { isMobile } from "react-device-detect";
import { Header } from "antd/es/layout/layout";
import { ApolloError } from "@apollo/client";

import "./main-navigation-layout.css";
import { useGetUserQuery } from "../../graphql/queries/getUser.generated";
import SidebarIcon, { SidebarIconsSrc } from "../sidebar-icon/sidebar-icon";
import routeNames from "../../constants/route-names";
import { useAuth } from "../../context/AuthContext";

const { Sider, Content } = Layout;

interface MenuItem {
  label: string;
  key: string;
  icon?: React.ReactNode;
  children?: MenuItem[];
}

const MainNavigationLayout: React.FC = () => {
  const { loggedUser, logout } = useAuth();
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const [collapsed, setCollapsed] = useState(false);
  const [aiChatRoute, setAiChatRoute] = useState("");

  const handleOnErrorsGetUser = useCallback((error: ApolloError) => {
    console.log("Error Get Users: ", error);
  }, []);

  const handleOnCompletedGetUser = useCallback((data: any) => {
    const isAdmin =
      data?.users_by_pk?.isHighnoteAdmin;

    setAiChatRoute(
      isAdmin ? routeNames.AI_CHATS : routeNames.AI_CHATS_NONADMIN
    );
  }, []);

  const { data, loading } = useGetUserQuery({
    skip: !loggedUser?.uid,
    variables: {
      id: loggedUser?.uid,
    },
    errorPolicy: "all",
    fetchPolicy: "network-only",
    nextFetchPolicy: "cache-first",
    notifyOnNetworkStatusChange: true,
    onError: handleOnErrorsGetUser,
    onCompleted: handleOnCompletedGetUser,
  });

  const handleLogout = useCallback(() => {
    if (logout) {
      logout();
    }
  }, [logout]);

  const menuItems: MenuItem[] = useMemo(() => {
    const baseItems: MenuItem[] = [
      {
        label: "HiGnome",
        key: aiChatRoute,
        icon: <SidebarIcon icon={SidebarIconsSrc.HIGNOME} />,
      },
      {
        label: "GRIT",
        key: routeNames.GRIT,
        icon: <SidebarIcon icon={SidebarIconsSrc.GRIT} />,
      },
    ];

    // Only add Mentorship and Manage if user is workspace admin
    if (
      data?.users_by_pk?.isHighnoteAdmin ||
      data?.users_by_pk?.isWorkspaceAdmin
    ) {
      baseItems.push(
        {
          label: "Programs",
          key: routeNames.MENTORSHIP,
          icon: <SidebarIcon icon={SidebarIconsSrc.MENTORSHIP} />,
        },
        {
          label: "Manage",
          key: routeNames.SETTINGS,
          icon: <SidebarIcon icon={SidebarIconsSrc.SETTINGS} />,
          children: [
            {
              label: "Users",
              key: routeNames.MANAGE_USERS,
            },
            // {
            //   label: "Workspace Users",
            //   key: routeNames.MENTORSHIP_USERS,
            // },
            {
              label: "Notifications",
              key: routeNames.CREATE_NOTIFICATION,
            },
          ],
        }
      );
    }

    return baseItems;
  }, [
    aiChatRoute,
    data?.users_by_pk?.isHighnoteAdmin,
    data?.users_by_pk?.isWorkspaceAdmin,
  ]);

  const selectedKeys = menuItems
    .flatMap((item) => [item, ...(item.children || [])])
    .filter((item) => pathname.includes(item.key))
    .map((item) => item.key);

  return (
    <Layout className="main-navigation-layout">
      <Sider
        width={285}
        collapsedWidth={isMobile ? 0 : 80}
        collapsible
        collapsed={collapsed}
        onCollapse={(value) => setCollapsed(value)}
        trigger={null}
        className="navigation-sider"
        breakpoint="lg"
        style={{
          width: "340px",
          maxWidth: "340px",
          minWidth: "340px",
          flex: "0 0 340px",
        }}
      >
        <div
          className={
            collapsed ? "sidebar-content-collapsed" : "sidebar-content"
          }
        >
          <div
            className="logo-container"
            // onClick={() => navigate(routeNames.HOME)}
          >
            <img
              src={
                process.env.PUBLIC_URL +
                "/assets/images/highnote-logo-white-bg.svg"
              }
              className="highnote-logo-nav"
              alt="Highnote Logo"
            />
            <div className="logo-full">Highnote</div>
          </div>
          {collapsed ? null : (
            <ConfigProvider
              theme={{
                components: {
                  Menu: {
                    itemColor: "#000000",
                    itemHoverColor: "#000000",
                    itemSelectedColor: "#000000",
                    groupTitleColor: "#000000",
                    subMenuItemSelectedColor: "#000000",
                    itemBg: "transparent",
                    itemHoverBg: "transparent",
                    itemActiveBg: "transparent",
                    itemSelectedBg: "transparent",
                    subMenuItemBg: "transparent",
                    popupBg: "transparent",
                    activeBarWidth: 0,
                    activeBarHeight: 0,
                    activeBarBorderWidth: 0,
                    itemHeight: 48,
                    itemPaddingInline: 24,
                    itemMarginBlock: 8,
                    itemMarginInline: 0,
                    iconSize: 14,
                    collapsedIconSize: 16,
                    collapsedWidth: 80,
                    darkItemBg: "transparent",
                    darkItemColor: "#000000",
                    darkItemHoverBg: "transparent",
                    darkItemSelectedBg: "transparent",
                    darkItemSelectedColor: "#000000",
                    darkSubMenuItemBg: "transparent",
                  },
                  Layout: {
                    siderBg: "transparent",
                    headerBg: "#f8f9fa",
                    bodyBg: "#f8f9fa",
                  },
                },
              }}
            >
              <Menu
                mode="inline"
                selectedKeys={selectedKeys}
                items={menuItems}
                className="navigation-menu"
                onClick={({ key }) => navigate(key)}
              />
            </ConfigProvider>
          )}
        </div>
      </Sider>

      <Layout className="site-layout">
        {/* <div
          className="sidebar-toggle"
          onClick={() => setCollapsed(!collapsed)}
        >
          {collapsed ? <RightOutlined /> : <LeftOutlined />}
        </div> */}
        <Header className="site-layout-header">
          <div className="header-content">
            <Button
              type="text"
              onClick={handleLogout}
              className="signout-button"
            >
              Sign Out
            </Button>
          </div>
        </Header>

        <Content className="content-container">
          <Outlet />
        </Content>
      </Layout>
    </Layout>
  );
};

export default MainNavigationLayout;
